import type { AdminV2EntryAction } from '@/admin-v2/entry-actions/entry-action';
import type {
  AdminV2ComponentButtonContract,
  AdminV2RepeatedContract,
} from '@/admin-v2/templates/component-design-contract';

type AdminV2PageButtonContractBlock = {
  blockInstanceId: string;
  buttons: AdminV2ComponentButtonContract[];
  repeated: Array<Pick<AdminV2RepeatedContract, 'initialItemIds' | 'key'>>;
};

export type AdminV2PageButtonContractStructure = {
  blocks: AdminV2PageButtonContractBlock[];
};

export type AdminV2PageButtonContractOwner = {
  blockInstanceId: string;
  buttonKey: string;
};

export type AdminV2ResolvedPageButtonContractOwner =
  AdminV2PageButtonContractOwner & {
    contract: AdminV2ComponentButtonContract;
    id: string;
  };

export type AdminV2PageButtonActionSelection =
  | { actionMode: 'none' }
  | { actionMode: 'pageDefault' }
  | { actionMode: 'existingAction'; existingActionId: string }
  | { actionMode: 'custom'; action: AdminV2EntryAction };

export type AdminV2PageDefaultEntryActionSelection =
  | { mode: 'none' }
  | { mode: 'existingAction'; actionId: string }
  | { mode: 'custom'; action: AdminV2EntryAction };

type AdminV2ResolvedEntryActionMap = Record<
  string,
  { action: AdminV2EntryAction }
>;

export function buildAdminV2PageButtonId(
  owner: AdminV2PageButtonContractOwner
) {
  return `${owner.blockInstanceId}.${owner.buttonKey}`;
}

export function buildAdminV2RepeatedPageButtonKey(params: {
  buttonKey: string;
  itemId: string;
}) {
  return `${params.buttonKey}:${params.itemId}`;
}

export function resolveAdminV2PageButtonByRuntimeIdentity<
  TButton extends AdminV2PageButtonContractOwner,
>(params: {
  buttons: readonly TButton[];
  blockInstanceId: string;
  buttonKey: string;
  repeatedItemId?: string;
}): TButton | null {
  const buttonKey = params.repeatedItemId
    ? buildAdminV2RepeatedPageButtonKey({
        buttonKey: params.buttonKey,
        itemId: params.repeatedItemId,
      })
    : params.buttonKey;

  return (
    params.buttons.find(
      (button) =>
        button.blockInstanceId === params.blockInstanceId &&
        button.buttonKey === buttonKey
    ) ?? null
  );
}

function listButtonKeys(params: {
  block: AdminV2PageButtonContractBlock;
  contract: AdminV2ComponentButtonContract;
}) {
  if (!params.contract.repeatedCollectionKey) {
    return [params.contract.key];
  }

  const collection = params.block.repeated.find(
    (candidate) => candidate.key === params.contract.repeatedCollectionKey
  );
  return (collection?.initialItemIds ?? []).map((itemId) =>
    buildAdminV2RepeatedPageButtonKey({
      buttonKey: params.contract.key,
      itemId,
    })
  );
}

export function listAdminV2PageButtonContractOwners(
  structure: AdminV2PageButtonContractStructure
): AdminV2ResolvedPageButtonContractOwner[] {
  return structure.blocks.flatMap((block) =>
    block.buttons.flatMap((contract) =>
      listButtonKeys({ block, contract }).map((buttonKey) => {
        const owner = {
          blockInstanceId: block.blockInstanceId,
          buttonKey,
        };
        return {
          ...owner,
          contract,
          id: buildAdminV2PageButtonId(owner),
        };
      })
    )
  );
}

export function listAdminV2RuntimeButtonContractOwners(params: {
  blockInstanceId: string;
  contracts: readonly AdminV2ComponentButtonContract[];
  repeatedItemIds: Readonly<Record<string, readonly string[]>>;
}): AdminV2ResolvedPageButtonContractOwner[] {
  return params.contracts.flatMap((contract) => {
    const buttonKeys = contract.repeatedCollectionKey
      ? (params.repeatedItemIds[contract.repeatedCollectionKey] ?? []).map(
          (itemId) =>
            buildAdminV2RepeatedPageButtonKey({
              buttonKey: contract.key,
              itemId,
            })
        )
      : [contract.key];

    return buttonKeys.map((buttonKey) => {
      const owner = {
        blockInstanceId: params.blockInstanceId,
        buttonKey,
      };
      return {
        ...owner,
        contract,
        id: buildAdminV2PageButtonId(owner),
      };
    });
  });
}

export function buildAdminV2PageComponentRepeatedItemIds(
  structure: AdminV2PageButtonContractStructure
) {
  return Object.fromEntries(
    structure.blocks.flatMap((block) => {
      const collections = Object.fromEntries(
        block.repeated.flatMap((collection) =>
          block.buttons.some(
            (button) => button.repeatedCollectionKey === collection.key
          )
            ? [[collection.key, collection.initialItemIds]]
            : []
        )
      );
      return Object.keys(collections).length > 0
        ? [[block.blockInstanceId, collections]]
        : [];
    })
  );
}

export function resolveAdminV2PageButtonContract(
  structure: AdminV2PageButtonContractStructure,
  owner: AdminV2PageButtonContractOwner
): AdminV2ComponentButtonContract | null {
  const match = listAdminV2PageButtonContractOwners(structure).find(
    (candidate) =>
      candidate.blockInstanceId === owner.blockInstanceId &&
      candidate.buttonKey === owner.buttonKey
  );
  return match?.contract ?? null;
}

export type AdminV2PageButtonWithConfiguredTarget = Exclude<
  AdminV2PageButtonActionSelection,
  { actionMode: 'none' }
>;

export function isAdminV2PageButtonTargetConfigured(
  button: AdminV2PageButtonActionSelection,
  defaultEntryAction: AdminV2PageDefaultEntryActionSelection
): button is AdminV2PageButtonWithConfiguredTarget {
  if (button.actionMode === 'none') {
    return false;
  }
  if (button.actionMode === 'pageDefault') {
    return defaultEntryAction.mode !== 'none';
  }
  return true;
}

export function resolveAdminV2PageButtonAction(params: {
  button: AdminV2PageButtonActionSelection;
  defaultEntryAction: AdminV2PageDefaultEntryActionSelection;
  resolvedEntryActions: AdminV2ResolvedEntryActionMap;
}): AdminV2EntryAction | null {
  if (params.button.actionMode === 'none') {
    return null;
  }
  if (params.button.actionMode === 'custom') {
    return params.button.action;
  }
  if (params.button.actionMode === 'existingAction') {
    return (
      params.resolvedEntryActions[params.button.existingActionId]?.action ??
      null
    );
  }

  const defaultEntryAction = params.defaultEntryAction;
  if (defaultEntryAction.mode === 'none') {
    return null;
  }
  if (defaultEntryAction.mode === 'custom') {
    return defaultEntryAction.action;
  }
  return (
    params.resolvedEntryActions[defaultEntryAction.actionId]?.action ?? null
  );
}

export function resolveAdminV2PageButtonMaterialization(params: {
  structure: AdminV2PageButtonContractStructure;
  defaultEntryAction: AdminV2PageDefaultEntryActionSelection;
  button: AdminV2PageButtonContractOwner &
    AdminV2PageButtonActionSelection & {
      label: Record<string, string | undefined>;
    };
}) {
  const contract = resolveAdminV2PageButtonContract(
    params.structure,
    params.button
  );
  const hasLabel = Object.values(params.button.label).some((label) =>
    Boolean(label?.trim())
  );
  const hasConfiguredTarget = isAdminV2PageButtonTargetConfigured(
    params.button,
    params.defaultEntryAction
  );
  return {
    contract,
    hasConfiguredTarget,
    hasLabel,
    materialized:
      !contract || contract.required || (hasLabel && hasConfiguredTarget),
  };
}

export function isAdminV2PageButtonMaterialized(
  params: Parameters<typeof resolveAdminV2PageButtonMaterialization>[0]
) {
  return resolveAdminV2PageButtonMaterialization(params).materialized;
}
